import { ChangeDetectionStrategy, Component, NgZone, afterNextRender, computed, input, signal } from '@angular/core';
import { CheckoutEventNames, initializePaddle } from '@paddle/paddle-js';
import { EmptyApiObject, IApiResponse } from 'api/api-models.js';
import { PaymentApi } from 'api/payment/payment-api.js';
import { UserApi } from 'api/user/user-api.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { angularErrorHelper } from 'lib/helpers/angular-error-helper.class.js';
import { consoleHelper } from 'lib/helpers/console-helper.class.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { Observable, from, map } from 'rxjs';
import { environment } from 'src/app/environments/environment.js';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';
import { PricingInterval } from './pricing-card.component';

export type SubscriptionPlan = 'free' | 'premium';

@Component({
	templateUrl: './checkout.component.html',
	selector: 'mod-checkout',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class CheckoutComponent extends TypeCoreComponent<UserDb.IUserItem> {
	public readonly pricingInterval = input.required<PricingInterval>();
	public readonly title = computed(() => {
		return this.pricingInterval() === 'monthly'
			? 'Create new monthly subscription'
			: 'Create new yearly subscription';
	});

	private readonly paddlePriceIds: Record<SubscriptionPlan, { readonly monthly: string; readonly yearly: string }> = {
		free: {
			monthly: '',
			yearly: ''
		},
		premium: {
			monthly: environment.paddle.priceIds.premiumMonthly,
			yearly: environment.paddle.priceIds.premiumYearly
		}
	};

	private readonly paddleTransactionIdQueryString: string = '_ptxn';

	public readonly refundHref: string = `mailto:${environment.refundEmail}`;
	public readonly refundEmail: string = `${environment.refundEmail}`;

	public readonly paddleContainerClassName: string = 'w-paddle-container';

	public readonly subscriptionPlan = signal<SubscriptionPlan>('premium');

	constructor(private ngZone: NgZone) {
		super();

		afterNextRender(() => {
			this.handleSubscribeToPlan(this.subscriptionPlan());
		});
	}

	private handleSubscribeToPlan(plan: SubscriptionPlan): void {
		const priceId = this.paddlePriceIds[plan][this.pricingInterval()];

		this.initiatePaddleCheckout({
			priceId: priceId
		});
	}

	private initiatePaddleCheckout(data: { priceId: string }): void {
		const authUser = this.authUserRequired();

		super.subscribeToObservable(
			from(
				initializePaddle({
					seller: environment.paddle.sellerId,
					environment: environment.paddle.isSandbox ? 'sandbox' : 'production',
					eventCallback: (event) => {
						// using timeout seems to be needed here as sometimes an exception like this is thrown:
						// NG0911: View has already been destroyed.
						setTimeout(() => {
							// execute 3rd party event in ngZone
							this.ngZone.run(() => {
								if (event.name === CheckoutEventNames.CHECKOUT_ERROR) {
									const errorData = angularErrorHelper.extractErrorData(event.error);
									consoleHelper.logToConsole({
										type: 'Error',
										message: `Paddle error: ${errorData.message}`,
										data: event.error
									});
									super.subscribeToObservable(
										this.dependencies.logService.logError({
											message: errorData.message,
											email: authUser.email,
											stacktrace: errorData.stacktrace,
											behavior: undefined
										})
									);
								} else if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
									super.subscribeToObservable(
										this.activateUserLicenseObs().pipe(
											map((response) => {
												super.updateSharedData({
													data: response.data.user,
													linkedItems: response.linkedItems
												});
												super.notify(`License activated`);
												this.dependencies.authService.updateAuthUser(response.data.token);
											})
										)
									);
								}
							});
						}, 50);
					}
				})
			).pipe(
				map((paddle) => {
					if (paddle) {
						const transactionId = this.getPaddleTransactionId();

						if (transactionId) {
							paddle.Checkout.open({
								transactionId: transactionId
							});
						} else {
							paddle.Checkout.open({
								settings: {
									frameTarget: this.paddleContainerClassName,
									theme: this.theme() === 'light' ? 'light' : 'dark',
									displayMode: 'inline',
									showAddDiscounts: true,
									frameInitialHeight: 450,
									frameStyle: 'min-width: 100%'
								},
								customer: {
									email: authUser.email
								},
								customData: this.getCustomData(authUser),
								items: [
									{
										priceId: data.priceId,
										quantity: 1
									}
								]
							});
						}
					}
				})
			)
		);
	}

	private getPaddleTransactionId(): string | undefined {
		if (!this.isBrowserPlatform()) {
			return undefined;
		}

		const url = location.href;

		return stringHelper.getQueryStringValue(this.paddleTransactionIdQueryString, url);
	}

	private getCustomData(authUser: UserDb.IAuthUser): PaymentApi.ICheckoutCustomData {
		return {
			email: authUser.email,
			userId: authUser.id
		};
	}

	private activateUserLicenseObs(): Observable<IApiResponse<UserApi.IUserResponseWithToken>> {
		return this.dependencies.httpService
			.post<UserApi.IUserResponseWithToken, EmptyApiObject>((builder) =>
				builder
					.withCustomApiRoute('user', 'activateLicense')
					.withData({})
					.withId(this.authUserRequired().id)
					.build()
			)
			.pipe(
				map((response) => {
					return response;
				})
			);
	}
}
